(() => {
  if (customElements.get('tt-navigation')) return;

  class TTNavigation extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.section = this.closest('.shopify-section-header');
      this.disclosure = this.querySelector('.tt-disclosure');
      this.toggle = this.querySelector('.tt-menu-toggle');
      this.surface = this.querySelector('.tt-menu-surface');
      this.bar = this.querySelector('.tt-bar');
      this.cartLink = this.querySelector('#cart-icon-bubble');
      this.groups = [...this.querySelectorAll('[data-tt-group]')];
      this.panels = [...this.querySelectorAll('[data-tt-panel]')];
      this.inertElements = [];
      this.panelAttributes = [];
      this.isDesktop = window.matchMedia('(min-width: 1200px)');

      this.addEventListener('click', event => this.onClick(event), { capture: true, signal });
      this.addEventListener('submit', event => this.onLanguageSubmit(event), { capture: true, signal });
      document.addEventListener('click', event => this.onSearchClick(event), { capture: true, signal });
      document.addEventListener('keydown', event => this.onKeyDown(event), { capture: true, signal });
      document.addEventListener('keyup', event => {
        if (event.key === 'Escape' && this.closedWithEscape) {
          this.closedWithEscape = false;
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        if (event.key === 'Escape' && document.querySelector('#search-modal.active')) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.closeSearch();
        }
      }, { capture: true, signal });
      document.addEventListener('focusin', event => {
        const activePanel = this.getActivePanel();
        if (activePanel && !activePanel.contains(event.target)) {
          const otherDialog = event.target.closest?.('[role="dialog"], [role="alertdialog"], [aria-modal="true"]');
          if (!otherDialog) this.focusPanel(activePanel);
          return;
        }
        if (!this.opened || this.contains(event.target)) return;
        if (event.target.closest?.('[role="dialog"], [role="alertdialog"], [aria-modal="true"], cart-drawer, account-modal, #search-modal')) {
          this.close(false);
        } else this.toggle?.focus({ preventScroll: true });
      }, { signal });
      window.addEventListener('pageshow', () => this.close(false), { signal });
      window.addEventListener('resize', () => this.updateDimensions(), { signal });
      window.visualViewport?.addEventListener('resize', () => this.updateDimensions(), { signal });
      window.visualViewport?.addEventListener('scroll', () => this.updateDimensions(), { signal });
      this.isDesktop.addEventListener('change', () => {
        this.activateGroup(this.groups[0]?.dataset.ttGroup);
        this.updateDimensions();
        if (this.opened && !this.isVisible(document.activeElement)) this.toggle.focus({ preventScroll: true });
      }, { signal });
      this.disclosure?.addEventListener('toggle', () => {
        if (this.disclosure.open !== Boolean(this.opened)) {
          this.disclosure.open ? this.open() : this.close(false);
        }
      }, { signal });
      document.addEventListener('shopify:section:load', event => {
        if (!event.target.contains(this)) return;
        this.rebindThemeDrawers();
        this.updateDimensions();
      }, { signal });
      document.addEventListener('shopify:section:unload', event => {
        if (event.target.contains(this)) this.close(false);
      }, { signal });

      this.resizeObserver = new ResizeObserver(() => this.updateDimensions());
      this.resizeObserver.observe(this.bar);
      this.overlayObserver = new MutationObserver(() => {
        const panel = this.getActivePanel();
        if (this.opened && panel) this.close(false);
        if (panel !== this.activePanel) {
          this.activePanel = panel;
          clearTimeout(this.panelFocusTimer);
          if (panel) this.focusPanel(panel);
        }
      });
      document.querySelectorAll('cart-drawer, account-modal, #search-modal').forEach(panel => {
        this.overlayObserver.observe(panel, { attributes: true, attributeFilter: ['class'] });
      });
      const account = document.querySelector('account-modal');
      const accountMain = account?.querySelector('.account__main');
      const setMissingAttribute = (element, name, value) => {
        if (!element || element.hasAttribute(name)) return;
        element.setAttribute(name, value);
        this.panelAttributes.push([element, name]);
      };
      // The original account drawer focuses this div after its transition.
      setMissingAttribute(accountMain, 'tabindex', '-1');
      setMissingAttribute(account, 'role', 'dialog');
      setMissingAttribute(account, 'aria-modal', 'true');
      const accountTitle = account?.querySelector('.account__header-title');
      if (accountTitle) setMissingAttribute(account, 'aria-label', accountTitle.textContent.trim());
      this.cartObserver = new MutationObserver(() => this.updateCartLabel());
      if (this.cartLink) this.cartObserver.observe(this.cartLink, { childList: true, subtree: true, characterData: true });
      this.activateGroup(this.groups[0]?.dataset.ttGroup);
      this.toggle?.setAttribute('aria-expanded', 'false');
      this.updateDimensions();
      this.updateCartLabel();
      if (this.disclosure?.open) this.open();
    }

    disconnectedCallback() {
      this.close(false);
      if (this.ownsSearch) this.closeSearch(false);
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      this.overlayObserver?.disconnect();
      this.cartObserver?.disconnect();
      clearTimeout(this.panelFocusTimer);
      this.panelAttributes?.forEach(([element, name]) => element.removeAttribute(name));
    }

    isVisible(element) {
      return element instanceof HTMLElement && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden';
    }

    getActivePanel() {
      return document.querySelector('cart-drawer.active, account-modal.active, #search-modal.active');
    }

    onLanguageSubmit(event) {
      const form = event.target;
      if (!form.matches?.('.tt-language-form')) return;
      const language = event.submitter?.value;
      const alternate = [...document.querySelectorAll('link[rel="alternate"][hreflang]')]
        .find(link => link.getAttribute('hreflang') === language);
      const returnTo = form.querySelector('input[name="return_to"]');
      if (!alternate || !returnTo) return;
      // Use Shopify's translated URL, including translated handles. Do not guess a prefix.
      const destination = new URL(alternate.href, window.location.href);
      // In a theme preview, shop.url uses the preview domain but hreflang uses
      // the published domain. Only copy the path; the form stays on this origin.
      if (!['http:', 'https:'].includes(destination.protocol) || destination.pathname.startsWith('//')) return;
      returnTo.value = destination.pathname + (destination.search || window.location.search);
    }

    getFocusable(container) {
      return [...container.querySelectorAll('summary, a[href], button:not(:disabled), select:not(:disabled), input:not([type="hidden"]):not(:disabled), textarea:not(:disabled), [tabindex="0"]')]
        .filter(element => this.isVisible(element) && !element.closest('[inert]'));
    }

    focusPanel(panel, attempt = 0) {
      clearTimeout(this.panelFocusTimer);
      if (!this.isConnected || !panel.classList.contains('active')) return;
      const preferred = panel.querySelector('.search__input, .drawer__close, .account__close');
      const target = this.isVisible(preferred) ? preferred : this.getFocusable(panel)[0];
      if (target) target.focus({ preventScroll: true });
      // Visibility transitions must finish before a browser can focus the input.
      if (!panel.contains(document.activeElement) && attempt < 20) {
        this.panelFocusTimer = setTimeout(() => this.focusPanel(panel, attempt + 1), 25);
      }
    }

    lockPage() {
      this.scrollPosition = { x: window.scrollX, y: window.scrollY };
      if (this.section) {
        const rect = this.section.getBoundingClientRect();
        this.section.style.setProperty('--tt-section-top', `${Math.max(0, rect.top)}px`);
        this.spacer = document.createElement('div');
        this.spacer.className = 'tt-header-spacer';
        this.spacer.setAttribute('aria-hidden', 'true');
        this.spacer.style.height = `${this.section.offsetHeight}px`;
        this.section.before(this.spacer);
      }
      document.body.style.setProperty('--tt-scroll-lock-top', `${-this.scrollPosition.y}px`);
      this.section?.classList.add('tt-navigation-active');
      document.documentElement.classList.add('tt-navigation-open');
      document.body.classList.add('tt-navigation-open');
    }

    unlockPage() {
      this.section?.classList.remove('tt-navigation-active');
      document.documentElement.classList.remove('tt-navigation-open');
      document.body.classList.remove('tt-navigation-open');
      this.spacer?.remove();
      this.spacer = null;
      this.section?.style.removeProperty('--tt-section-top');
      document.body.style.removeProperty('--tt-scroll-lock-top');
      if (this.scrollPosition) {
        window.scrollTo({ left: this.scrollPosition.x, top: this.scrollPosition.y, behavior: 'instant' });
        this.scrollPosition = null;
      }
    }

    updateDimensions() {
      if (!this.bar) return;
      const rect = this.bar.getBoundingClientRect();
      const viewport = window.visualViewport;
      const bottom = Math.max(0, rect.bottom);
      const viewportBottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
      this.style.setProperty('--tt-header-bottom', `${bottom}px`);
      this.style.setProperty('--tt-available-height', `${Math.max(0, viewportBottom - bottom)}px`);
      if (this.section) document.documentElement.style.setProperty('--header-height', `${Math.round(rect.height)}px`);
    }

    activateGroup(id) {
      const selected = this.groups.find(button => button.dataset.ttGroup === id) || this.groups[0];
      this.groups.forEach(button => {
        const active = button === selected;
        button.setAttribute('aria-expanded', String(active));
        // One desktop panel stays open; the active control cannot collapse it.
        button.setAttribute('aria-disabled', String(active));
      });
      this.panels.forEach(panel => { panel.hidden = panel.dataset.ttPanel !== selected?.dataset.ttGroup; });
    }

    updateCartLabel() {
      if (!this.cartLink) return;
      const counter = this.cartLink.querySelector('.cart-count-bubble');
      const countText = counter?.querySelector('[aria-hidden="true"]')?.textContent || counter?.textContent;
      const count = countText?.match(/\d+/)?.[0];
      if (count !== undefined) this.cartLink.setAttribute('aria-label', `${this.cartLink.dataset.ttCartLabel} (${count})`);
    }

    rebindThemeDrawers() {
      if (this.rebound) return;
      this.rebound = true;
      // Header replacement in the theme editor leaves these drawers in the layout.
      for (const tag of ['cart-drawer', 'account-modal']) {
        const drawer = document.querySelector(tag);
        const id = tag === 'cart-drawer' ? '#cart-icon-bubble' : '#account-icon-open';
        if (this.querySelector(id) && typeof drawer?.setHeaderCartIconAccessibility === 'function') drawer.setHeaderCartIconAccessibility();
      }
    }

    closeOtherPanels(except) {
      for (const tag of ['cart-drawer', 'account-modal']) {
        if (tag === except) continue;
        const drawer = document.querySelector(`${tag}.active`);
        if (typeof drawer?.close === 'function') drawer.close();
      }
      if (except !== 'search') {
        this.closeSearch(false);
      }
    }

    onSearchClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const trigger = target.closest('.search-modal-trigger');
      const closer = target.closest('#search-modal .search-modal__overlay, #search-modal .search__close');
      if (!(trigger && this.contains(trigger)) && !closer) return;
      const search = document.querySelector('#search-modal');
      if (!search) return;
      if (trigger && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0)) return;
      // The theme binds its existing search buttons again on every section load.
      // Capture here so its unchanged search panel is controlled exactly once.
      event.preventDefault();
      event.stopImmediatePropagation();
      if (closer || search.classList.contains('active')) {
        this.closeSearch();
        return;
      }
      this.close(false);
      this.closeOtherPanels('search');
      this.searchReturnTarget = trigger;
      this.ownsSearch = true;
      search.classList.add('active');
      document.body.classList.add('overflow-hidden');
      const input = search.querySelector('.search__input');
      if (typeof trapFocus === 'function') trapFocus(search, input || search);
      else input?.focus();
      this.focusPanel(search);
    }

    closeSearch(restoreFocus = true) {
      const search = document.querySelector('#search-modal.active');
      if (!search) return;
      search.classList.remove('active');
      this.ownsSearch = false;
      if (!document.querySelector('cart-drawer.active, account-modal.active')) {
        document.body.classList.remove('overflow-hidden');
        if (typeof removeTrapFocus === 'function') removeTrapFocus();
      }
      const target = this.searchReturnTarget?.isConnected ? this.searchReturnTarget : this.querySelector('.tt-search');
      if (restoreFocus) target?.focus({ preventScroll: true });
    }

    open() {
      if (!this.disclosure || this.opened) return;
      this.closeOtherPanels();
      this.activateGroup(this.groups[0]?.dataset.ttGroup);
      this.opened = true;
      this.disclosure.open = true;
      this.setAttribute('data-open', '');
      this.setAttribute('role', 'dialog');
      this.setAttribute('aria-modal', 'true');
      this.setAttribute('aria-label', this.toggle.getAttribute('aria-label') || 'Menu');
      this.toggle.setAttribute('aria-expanded', 'true');
      this.lockPage();
      // Do not change the theme's overflow-hidden or its shared focus-trap state.
      this.inertElements = [...document.querySelectorAll('#MainContent, .shopify-section-group-footer-group, .section-announcement')]
        .filter(element => !element.contains(this) && !element.inert);
      this.inertElements.forEach(element => { element.inert = true; });
      this.updateDimensions();
      this.toggle.focus({ preventScroll: true });
    }

    close(restoreFocus = true) {
      if (!this.opened) return;
      this.opened = false;
      if (this.disclosure) this.disclosure.open = false;
      this.removeAttribute('data-open');
      this.removeAttribute('role');
      this.removeAttribute('aria-modal');
      this.removeAttribute('aria-label');
      this.toggle?.setAttribute('aria-expanded', 'false');
      this.unlockPage();
      this.inertElements.forEach(element => { element.inert = false; });
      this.inertElements = [];
      if (restoreFocus && this.toggle?.isConnected) this.toggle.focus({ preventScroll: true });
    }

    onClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target.closest('.tt-menu-toggle')) {
        event.preventDefault();
        this.opened ? this.close() : this.open();
        return;
      }
      if (target.closest('[data-tt-close]')) {
        this.close();
        return;
      }
      const group = target.closest('[data-tt-group]');
      if (group) {
        this.activateGroup(group.dataset.ttGroup);
        return;
      }
      const account = target.closest('[data-tt-account]');
      const cart = target.closest('#cart-icon-bubble');
      if (account || cart) {
        const kind = account ? 'account-modal' : 'cart-drawer';
        const drawer = document.querySelector(kind);
        const trigger = account || cart;
        const plainNavigation = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0;
        if (!plainNavigation && typeof drawer?.open === 'function') {
          event.preventDefault();
          event.stopImmediatePropagation();
          const returnFocus = trigger.classList.contains('tt-mobile-account') ? this.toggle : trigger;
          this.close(false);
          this.closeOtherPanels(kind);
          drawer.open(returnFocus);
          return;
        }
      }
      if (target.closest('a[href]')) this.close(false);
    }

    onKeyDown(event) {
      const activePanel = this.getActivePanel();
      if (activePanel) {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.closedWithEscape = true;
          if (activePanel.id === 'search-modal') this.closeSearch();
          else activePanel.close?.();
        } else if (event.key === 'Tab') {
          const focusable = this.getFocusable(activePanel);
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (first && (!activePanel.contains(document.activeElement) ||
            (event.shiftKey && document.activeElement === first) ||
            (!event.shiftKey && document.activeElement === last))) {
            event.preventDefault();
            event.stopImmediatePropagation();
            (event.shiftKey ? last : first).focus({ preventScroll: true });
          }
        }
        return;
      }
      if (!this.opened) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        const nested = event.target.closest?.('.tt-accordion[open]');
        if (nested) {
          nested.open = false;
          nested.querySelector('summary')?.focus({ preventScroll: true });
        } else this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = this.getFocusable(this);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first) return;
      if (event.shiftKey && (document.activeElement === first || !this.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !this.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('tt-navigation', TTNavigation);
})();
